/**
 * Vuex store for the upstream refining contract catalog (UCG-2210
 * contract-management slice).
 *
 * Session cache for the API-backed upstream contract catalog.
 * Persistence lives in Platform Gold; this module is not on vuex-persist.
 *
 * Partners remain create-via-API / read-in-editor (no partner editor yet).
 */

import {
  isContractEffective,
  isContractSelectableForSwitch,
} from '@/util/refinery/upstream/upstreamContract'
import {
  fetchUpstreamRefiningContracts as apiFetchContracts,
  fetchRefiningPartners as apiFetchPartners,
  createContract as apiCreateContract,
  updateContract as apiUpdateContract,
  setContractActive as apiSetContractActive,
} from '@/api/refinery/upstreamRefiningContractsApi'

const getters = {
  getContracts: (state) =>
    Object.values(state.contracts).sort((a, b) =>
      a.title.toLowerCase().localeCompare(b.title.toLowerCase())
    ),
  getActiveContracts: (_state, accessors) =>
    accessors.getContracts.filter((contract) => isContractEffective(contract)),
  getSelectableContractsForSwitch: (_state, accessors) =>
    accessors.getContracts.filter((contract) => isContractSelectableForSwitch(contract)),
  getContractById: (state) => (id) => state.contracts[id] || null,
  getRefiningPartners: (state) => state.partners,
  isCatalogSeeded: (state) => state.seeded,
}

const actions = {
  async bootstrapIfEmpty({commit, state}) {
    if (state.seeded) return Object.values(state.contracts)
    const [contracts, partners] = await Promise.all([
      apiFetchContracts(),
      apiFetchPartners(),
    ])
    commit('SET_CONTRACTS', contracts)
    commit('SET_REFINING_PARTNERS', partners)
    commit('SET_SEEDED', true)
    return contracts
  },

  async fetchContracts({state, dispatch}) {
    if (!state.seeded) await dispatch('bootstrapIfEmpty')
    return Object.values(state.contracts)
  },

  async fetchRefiningPartners({state, dispatch}) {
    if (!state.seeded) await dispatch('bootstrapIfEmpty')
    return state.partners
  },

  // Draft validation lives at the API boundary. The store stays thin.
  async createContract({commit}, draft) {
    const contract = await apiCreateContract(draft)
    commit('UPSERT_CONTRACT', contract)
    return contract
  },

  async updateContract({commit, state}, {id, draft}) {
    const existing = state.contracts[id]
    if (!existing) throw new Error(`upstream refining contract not found: ${id}`)
    const contract = await apiUpdateContract(existing, draft)
    commit('UPSERT_CONTRACT', contract)
    return contract
  },

  async setContractActive({commit, state}, {id, active}) {
    const existing = state.contracts[id]
    if (!existing) throw new Error(`upstream refining contract not found: ${id}`)
    const contract = await apiSetContractActive(existing, active)
    commit('UPSERT_CONTRACT', contract)
    return contract
  },

  async resetCatalog({commit}) {
    commit('RESET_STATE')
    const [contracts, partners] = await Promise.all([
      apiFetchContracts(),
      apiFetchPartners(),
    ])
    commit('SET_CONTRACTS', contracts)
    commit('SET_REFINING_PARTNERS', partners)
    commit('SET_SEEDED', true)
    return contracts
  },
}

const mutations = {
  SET_CONTRACTS(state, contracts) {
    const next = {}
    for (const contract of contracts) {
      next[contract.id] = contract
    }
    state.contracts = next
  },
  UPSERT_CONTRACT(state, contract) {
    state.contracts = {...state.contracts, [contract.id]: contract}
  },
  SET_REFINING_PARTNERS(state, partners) {
    state.partners = partners
  },
  SET_SEEDED(state, value) {
    state.seeded = value
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
}

export default {
  namespaced: true,
  state: getDefaultState,
  getters,
  actions,
  mutations,
}

function getDefaultState() {
  return {
    contracts: {},
    partners: [],
    seeded: false,
  }
}

export const __testing__ = {
  getDefaultState,
}
